<script setup lang="ts">
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { RouterView } from 'vue-router'
import { computed, reactive, toRef, watch } from 'vue'
import { useUserSettingsStore } from '@/stores/userSettings'
import { useLoadingRouteStore } from '@/stores/loadingRoute'
import { useI18n } from 'vue-i18n'
import SideBar from '@/components/layout/SideBar.vue'
import Spinner from '@/components/ui/spinner/Spinner.vue'
import { useUserStore } from './stores/userStore'
import { useApiQuery } from './composables/useApiQuery'
import { useAuthStore } from './stores/auth'
import { CURRENCY_LIST, USER_AVATAR_LIST } from './services/apiModules'
import { useSetupSettingsStore } from './stores/setupStore'
import TopBarMobile from '@/components/layout/TopBarMobile.vue'
import BottomBarMobile from '@/components/layout/BottomBarMobile.vue'
import { useBreakpoints } from './utils/useBreakpoints'

const DEFAULT_TITLE = 'TWLT Knoten punkt'

const isMobile = useBreakpoints().smaller('md')

const userSettingsStore = useUserSettingsStore()
userSettingsStore.updateSettings({ theme: userSettingsStore.theme || 'light' })
const authStore = useAuthStore()
const userStore = useUserStore()
const setupStore = useSetupSettingsStore()
const loading = useLoadingRouteStore()
const user = reactive({
  company: toRef(userStore, 'firma'),
})
const enabled = computed(() => authStore.isLoggedIn && !!userStore.firma)

const { data: avatarListData, isFetching: isAvatarListLoading } = useApiQuery({
  modus: USER_AVATAR_LIST,
  enabled,
  staleTime: 1 * 60 * 60 * 1000, // 1 hours
})

watch(avatarListData, (newData) => {
  const userAvatarList = Array.isArray(newData)
    ? newData.filter(
        (item): item is { id: number; avatar: string } =>
          typeof item.id === 'number' && typeof item.avatar === 'string',
      )
    : []

  setupStore.updateSetupSettings({ userAvatarList })
})

const { data: currencyListData, isFetching: isCurrencyListLoading } = useApiQuery({
  modus: CURRENCY_LIST,
  enabled,
  staleTime: 1 * 60 * 60 * 1000, // 1 hours
})

watch(currencyListData, (newData) => {
  const currencyList = Array.isArray(newData)
    ? newData.filter(
        (item): item is { currency: string; icon: string } =>
          typeof item.currency === 'string' && typeof item.icon === 'string',
      )
    : []
  setupStore.updateSetupSettings({ currencyList })
})

const { locale } = useI18n()
locale.value = userSettingsStore.language

watch(
  () => userSettingsStore.language,
  (newLang) => {
    locale.value = newLang
  },
)

watch(
  () => user.company,
  (newName) => {
    document.title = newName || DEFAULT_TITLE
  },
  { immediate: true },
)
</script>

<template>
  <VueQueryDevtools />
  <div class="fixed inset-0 flex h-full flex-col transition-[height] duration-350 ease-out">
    <TopBarMobile v-if="isMobile" class="block" />
    <div class="flex h-full flex-1 flex-row gap-0 overflow-y-hidden">
      <SideBar v-if="!isMobile" class="flex shrink-0" />

      <div class="relative h-full w-full overflow-y-hidden rounded-md">
        <Spinner
          v-if="loading.isLoading || isAvatarListLoading || isCurrencyListLoading"
          :overlay="true"
        />
        <div class="mb-16 h-full md:mb-0">
          <RouterView />
        </div>
      </div>
    </div>
    <BottomBarMobile v-if="isMobile" class="block shrink-0" />
  </div>
</template>
